/**
 * `move_actor` — the one way a body's location changes (D233, actors round
 * C; `docs/design/actors.md` §5).
 *
 * @remarks
 * An actor is an embodied thing with a location that can CHANGE — the clause
 * that separates it from an item, whose location changes only by the
 * player's hands. This module is that clause. Three rules, each the reason
 * for a line below:
 *
 * - **The record is a delta.** `world.actorLocations` holds only the actors
 *   that stand somewhere other than where the pack put them (the "no derived
 *   data" rule `droppedItemLocations` follows for things). A body moved back
 *   to its declared room — or offstage, for one that declares none — leaves
 *   the record rather than being written into it as "where it already was",
 *   so the save says exactly what moved and a replay's photograph has nothing
 *   redundant to disagree with.
 * - **A move is witnessed or it is silent.** The player sees a body enter
 *   the room they stand in, or leave it, and nothing else: an actor walking
 *   two rooms away makes no line. Witnessed, the actor's `arrivalNarratives`
 *   or `departureNarratives` speak in the turn under
 *   `actor_moved:<actorId>`, so the transcript records WHO moved, as every
 *   other provenance tag records who spoke (R11). A body the player cannot
 *   see (its `visibleWhen` unmet) arrives and leaves unseen too — the
 *   catalog does not know it is there, and the transcript must not say so.
 * - **A move to where the body already stands is nothing.** No record, no
 *   line: a repeatable trigger that moves the guard to the post he is
 *   already at does not announce him twice.
 *
 * The catalog is derived per turn, so a body that arrives mid-turn is
 * referable the turn after its arrival is spoken — which is the right
 * reading: the line is the player noticing, and noticing comes first.
 *
 * @packageDocumentation
 */

import type { ActorDefinition, ActorDestination, ActorId, MoveActorEffect, RoomId, RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireActor } from '../bundle/bundle-lookups.js';
import { type StampedNarratives, stampNarrativeLines, type TranscriptCursor } from '../execution/transcript-cursor.js';
import { isActorVisible, roomOfActor } from './actor-presence.js';

/** The result of one move: the next state, any witnessed lines, and the advanced cursor. */
export interface ActorMove extends StampedNarratives {
	readonly state: RuntimeState;
}

/**
 * The provenance tag a witnessed move's lines carry — `actor_moved:<actorId>`.
 *
 * @param actorId - The actor that moved
 * @returns The tag
 */
export const actorMovedProvenance = (actorId: ActorId): string => `actor_moved:${actorId}`;

/**
 * Where the pack itself put the actor — its `initialRoomId`, or offstage.
 *
 * @param actor - The actor
 * @returns The declared location, in the record's vocabulary
 */
const declaredLocationOf = (actor: ActorDefinition): ActorDestination => actor.initialRoomId ?? 'offstage';

/**
 * The moved record with this actor's location written as a DELTA against its
 * declaration: a body back where the pack put it leaves the record.
 *
 * @remarks
 * The one writer of `world.actorLocations`: a `move_actor` writes through it,
 * and so does a follow (D238, `actor-following.ts`) — a body led is a body
 * moved, recorded the same way, so the save and the photograph cannot tell
 * how it got there and need not.
 *
 * @param actorLocations - The moved record before
 * @param actor - The actor
 * @param destination - Where it now stands
 * @returns The next moved record
 */
export const withActorLocation = (
	actorLocations: RuntimeState['world']['actorLocations'],
	actor: ActorDefinition,
	destination: ActorDestination,
): RuntimeState['world']['actorLocations'] => {
	const { [actor.actorId]: _previous, ...others } = actorLocations;

	return destination === declaredLocationOf(actor) ? others : { ...others, [actor.actorId]: destination };
};

/**
 * Apply one `move_actor`: write the delta, and speak the move to a player
 * who sees it.
 *
 * @param effect - The move
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE the move
 * @param cursor - Where any witnessed line lands in the transcript
 * @returns The next state, the witnessed lines, and the advanced cursor
 */
export const moveActor = (effect: MoveActorEffect, bundle: EngineGameBundle, state: RuntimeState, cursor: TranscriptCursor): ActorMove => {
	const actor = requireActor(bundle, effect.actorId);
	const standsIn: RoomId | undefined = roomOfActor(bundle, state, effect.actorId);
	const destination: RoomId | undefined = effect.destination === 'offstage' ? undefined : effect.destination;

	if (standsIn === destination) {
		return { cursor, entries: [], state };
	}

	const nextState: RuntimeState = {
		...state,
		world: { ...state.world, actorLocations: withActorLocation(state.world.actorLocations, actor, effect.destination) },
	};

	// The player witnesses the move only where they stand, and only a body
	// they could see: an unseen actor is not in the catalog, and the
	// transcript must not know more than the catalog does.
	if (!isActorVisible(bundle, state, actor)) {
		return { cursor, entries: [], state: nextState };
	}

	const playerRoomId = state.progression.activeRoomId;
	const witnessedLines = destination === playerRoomId ? actor.arrivalNarratives : standsIn === playerRoomId ? actor.departureNarratives : undefined;

	if (witnessedLines === undefined) {
		return { cursor, entries: [], state: nextState };
	}

	return { ...stampNarrativeLines(bundle, nextState, cursor, witnessedLines, actorMovedProvenance(effect.actorId)), state: nextState };
};
